import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button as UiButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { DealState, ExportTemplate } from '@/lib/graphql/generated/graphql';
import { useDealSelectionExport } from '@/lib/hooks/useDealSelectionExport';
import Button from '@appchoose/button';
import { FileSpreadsheet, FolderOutput, Loader2 } from 'lucide-react';

function ExportButton() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    deal: { id: dealId, ...deal },
  } = useDealContext();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const template = ExportTemplate.Sale;
  const { isExporting, downloadUrl, error, startExport } =
    useDealSelectionExport(dealId, template);
  const user = useUser();
  const isInternalUser = user?.isInternalUser;

  const title = deal?.hubspotInfos?.dealName ?? '';
  const isClosedDeal = deal.state === DealState.NegotiationClosed;

  const handleClick = useCallback(() => {
    if (isClosedDeal && isInternalUser) {
      setIsExportDialogOpen(true);
      return;
    }

    setIsInteracting(true);
    startExport();
  }, [isClosedDeal, startExport, isInternalUser]);

  const handleClosedExportSalesChoice = useCallback(() => {
    setIsExportDialogOpen(false);
    setIsInteracting(true);
    startExport();
  }, [startExport]);

  const handleClosedViewEmerchExportsChoice = useCallback(() => {
    setIsExportDialogOpen(false);
    navigate(`/deal/${dealId}/history?tab=exports`);
  }, [dealId, navigate]);

  useEffect(() => {
    if (!isInteracting) return;
    if (!downloadUrl) return;

    fetch(downloadUrl)
      .then((res) => {
        if (!res.ok) throw new Error(t('general.error_download'));
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = downloadUrl.match(/\.(\w+)(?:\?|$)/)?.[1] ?? 'csv';
        const date = new Date().toISOString().slice(0, 10);
        // For now we do not use eMerch export
        // const filename =
        //   template === ExportTemplate.Emerch
        //     ? `smart-offer-${title}.${extension}`
        //     : `${title}-Offre-${new Date().toISOString().slice(0, 10)}.${extension}`;
        const filename = `${title}-Offre-${date}.${extension}`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        toast({
          title: t('general.success_export'),
          description: t('general.success_export_csv_download'),
        });
      })
      .catch(() => {
        toast({
          title: t('general.error'),
          description: t('general.error_download'),
          variant: 'destructive',
        });
      })
      .finally(() => {
        setIsInteracting(false);
      });
  }, [downloadUrl, toast, title, isInteracting, template, t]);

  useEffect(() => {
    if (error) {
      toast({
        title: t('general.error'),
        description: error.message,
        variant: 'destructive',
      });
      setIsInteracting(false);
    }
  }, [error, toast, t]);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              data-user-guide="Download"
              onClick={handleClick}
              disabled={isExporting}
              appearance="outline"
              className="flex items-center gap-2 border border-gray-300 font-medium"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FolderOutput className="h-4 w-4" />
              )}
              {t('general.export')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('components.offer_actions_header.csv_export_button_tooltip')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('components.offer_actions_header.closed_export_dialog.title')}
            </DialogTitle>
            <DialogDescription className="whitespace-pre-line">
              {t(
                'components.offer_actions_header.closed_export_dialog.description'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-start sm:space-x-0">
            <UiButton
              variant="outline"
              onClick={handleClosedExportSalesChoice}
              className="flex w-1/2 items-center gap-2"
            >
              <FolderOutput className="h-4 w-4" />
              {t(
                'components.offer_actions_header.closed_export_dialog.export_sales'
              )}
            </UiButton>
            <UiButton
              onClick={handleClosedViewEmerchExportsChoice}
              className="flex w-1/2 items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {t(
                'components.offer_actions_header.closed_export_dialog.view_emerch_exports'
              )}
            </UiButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ExportButton;
